import { DEFAULT_INVOICE_PREFIX } from '@features/invoices/constants/defaultInvoicePrefix'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { InvoiceDiscountLocation } from '@features/invoices/enums/invoiceDiscountLocation'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import { InvoiceDraft } from '@features/invoices/types/invoiceDraft'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { InvoicePayloadRowLine } from '@features/invoices/types/invoicePayload/invoicePayloadRowLine'
import { getDisplayedPaymentMethods } from '@features/invoices/utils/getDisplayedPaymentMethods'
import { RecurringInvoiceDraft } from '@features/recurringInvoices/types/recurringInvoiceDraft'
import type { VatRate } from '@features/vatRates/types/vatRate'

import { NumberValue } from '../../../../../../utils'
import { getLinesValues } from './getLinesValues'
import { getMaxDateOfSaleOrServiceFromLines } from './getMaxDateOfSaleOrServiceFromLines'
import { getReceiverValues } from './getReceiverValues'

interface GetInvoiceEditorFormValuesFromDraftOptions {
  defaultInvoiceValues: InvoiceForm
  draft: InvoiceDraft | RecurringInvoiceDraft['draft']
  isQuote?: boolean
  isRecurringInvoice?: boolean
  vatRates?: VatRate[]
}

export const getInvoiceEditorFormValuesFromDraft = ({
  defaultInvoiceValues,
  draft,
  isQuote,
  isRecurringInvoice,
  vatRates,
}: GetInvoiceEditorFormValuesFromDraftOptions): InvoiceForm => {
  const { fields, id, invoiceNumber, lines, paymentMeans } = draft
  const {
    approvedEstimateId,
    buyerReference,
    dateOfSaleOrService,
    description,
    discountAmount,
    discountEnabled,
    discountLocation,
    discountType,
    dueDate,
    footerText,
    issueDate,
    language,
    paymentTime,
    purchaseOrderNumber,
  } = fields
  const rowLines = lines.filter((line) => line.type === InvoiceLineType.Row) as InvoicePayloadRowLine[]
  const invoiceNumberValue = invoiceNumber?.number ?? (isQuote ? defaultInvoiceValues.invoiceNumber : undefined)
  const invoiceNumberPrefix = invoiceNumber?.prefix === null ? DEFAULT_INVOICE_PREFIX : invoiceNumber?.prefix
  const defaultDateOfSaleOrService = isQuote || isRecurringInvoice ? null : getMaxDateOfSaleOrServiceFromLines(lines)
  const currency = rowLines[0]?.currency ?? defaultInvoiceValues.currency
  const resolvedDiscountType = (discountEnabled?.value && discountType?.value) || defaultInvoiceValues.discountType

  return {
    ...defaultInvoiceValues,
    ...(approvedEstimateId && {
      approvedEstimateId: String(approvedEstimateId.value),
    }),
    buyerReference: buyerReference?.value,
    currency,
    dateOfSaleOrService: dateOfSaleOrService?.value ? new Date(dateOfSaleOrService.value) : defaultDateOfSaleOrService,
    displayedPaymentMethods: getDisplayedPaymentMethods(paymentMeans, defaultInvoiceValues.displayedPaymentMethods),
    ...(discountEnabled?.value && {
      discountType: resolvedDiscountType,
      // The global discount amount only applies in PerTotal mode - PerLine discounts are
      // restored per-line from each line's own `discount` field in getLinesValues.
      ...(discountLocation?.value === InvoiceDiscountLocation.PerTotal &&
        discountAmount && {
          discount: Number(discountAmount.value),
        }),
    }),
    dueDate: dueDate?.value ? new Date(dueDate.value) : defaultInvoiceValues.dueDate,
    footerNote: footerText.value,
    ...(isRecurringInvoice ? {} : { id }),
    invoiceLines: getLinesValues({
      currency,
      defaultLineValues: defaultInvoiceValues.invoiceLines[0],
      discountType: resolvedDiscountType,
      isDiscountEnabled: discountEnabled?.value ?? true,
      lines,
      vatRates,
    }),
    invoiceNumber: invoiceNumberValue,
    invoiceNumberPrefix,
    issueDate: issueDate.value ? new Date(issueDate.value) : defaultInvoiceValues.issueDate,
    language: invoiceLanguageToLocale[language?.value] || defaultInvoiceValues.language,
    message: description?.value || '',
    paymentTerm: new NumberValue(paymentTime?.value ?? defaultInvoiceValues.paymentTerm).getValue(),
    purchaseOrderNumber: purchaseOrderNumber?.value,
    receiver: getReceiverValues(draft.receiver),
  }
}
